import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import i18n from '../../translation/i18n';
import IssueGuide from '../IssueGuide';
import { IUser } from '../../atoms';
import { apiGet } from '../../services/_common';
import { getAgeFromCertainDate } from '../../utils/getAge';
import SkeletonTable from '../SkeletonTable';
import { ERROR } from '../../_constants';
import styled from 'styled-components';

const Root = styled.div`
  height: 100%;
  position: relative;

  > .widget-header {
    width: 100%;
    padding: 0.75rem 1.5rem;
    > .widget-title {
      height: 2rem;
      > span {
        color: var(--text-primary);
        font-weight: var(--font-semibold);
        font-size: 1.125rem;
        line-height: 1.2;
      }
    }
    > .widget-subTitle {
      font-size: 0.875rem;
      line-height: 1rem;
      color: var(--text-secondary);
    }
  }

  > .widget-body {
    flex-grow: 1;
    width: 100%;
    padding: 0 1.5rem;
    margin-bottom: 1rem;
    gap: 1rem;

    user-select: none;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    > div {
      gap: 0.75rem;
      > div:nth-child(1) {
        width: 5rem;
        height: 5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0.5rem;
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 40;
          font-size: 3.5rem;
        }
      }
      > div.orange {
        color: #fb923c;
      }
      > div:nth-child(2) {
        display: flex;
        flex-direction: column;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--text-secondary);
        gap: 0.5rem;
        > div {
          font-size: 1.5rem;
          span {
            font-size: 3.5rem;
            font-weight: 800 !important;
            color: var(--widget-text-primary);
            font-weight: var(--font-semibold);
          }
        }
      }
    }
    > div.infoBox {
      gap: 0.5rem;
      font-size: 0.75rem;
      padding: 2.5rem;
      border-radius: 1rem;
      justify-content: space-between;
      background-color: var(--widget-sub-bg);

      > div:nth-child(2) {
        gap: 0.5rem;
        text-align: end;
        span {
          margin-right: 0.125rem;
        }
      }

      > .theme-blue {
        color: var(--widget-primary);
      }

      > .theme-blue-sky {
        color: var(--widget-secondary);
      }
    }
  }
  .widget-body.safety {
    gap: 1rem;
    margin: 1rem 0;
    > div:nth-child(1) {
      background-color: var(--tonal-orange);
    }
    > div:nth-child(2) {
      background-color: var(--tonal-red);
    }
    > div {
      border-radius: 0.25rem;
      display: flex;
      padding: 0 0.75rem;
      > div:nth-child(1) {
        width: 100%;
        text-align: left;
        justify-content: flex-start;
        height: 100%;
        align-items: flex-start;
        span {
          font-size: 3rem;
          padding-top: 0.5rem;
        }
      }
      > div:nth-child(2) {
        width: 100%;
        align-items: flex-end;
        gap: 0;
        height: 100%;
        justify-content: flex-end;
        padding-bottom: 0.75rem;
        span {
          font-size: 1.5rem;
        }
      }
    }
  }
`;

// 종합 현황
export const TotalInfoLarge = ({ name, userInfo, refetchInterval }: { name: string; userInfo: IUser; refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  dayjs.locale(i18n.language === 'en-US' ? 'en' : 'ko');
  const { t } = useTranslation();
  const { hCd, sCd } = userInfo;
  const todayjs = dayjs();
  const [currentDate, setCurrentDate] = useState(todayjs);
  const format = currentDate.format('YYYYMMDD');

  const {
    data: newWorkerData,
    isLoading: isLoadingNewWorkerData,
    isError: isErrorNewWorkerData,
    refetch: refetchNewWorkerData,
  } = useQuery(['TotalInfoNewWorkerGet', userInfo], () => fetchNewWorkerData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }), // 30분
    onSettled: () => {
      setCurrentDate(dayjs());
    },
  });
  const {
    data: attendData,
    isLoading: isLoadingAttendData,
    isError: isErrorAttendData,
    refetch: refetchAttendData,
  } = useQuery(['attendCntGet', userInfo], () => fetchAttendData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }), // 30분
  });

  // 금일 신규 근로자 인원 수 조회
  const fetchNewWorkerData = async () => {
    try {
      const req = { wWorkstatus: 'I', wJobdate1: format, wJobdate2: format, hCd, sCd };
      const res = await apiGet({ path: '/worker', req });
      if (res.status === 200) {
        const newWorkerCnt = res.data.data.workerList.length;
        return { newWorkerCnt };
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 금일 출역 요약 조회
  const fetchAttendData = async () => {
    try {
      const elderlyAge = userInfo.elderlyAgeSet || 65;
      const req = { aDate1: format, aDate2: format, hCd, sCd, elderlyAge: String(elderlyAge) };
      const res = await apiGet({ path: '/attend/day/summary', req });
      if (res.status === 200) {
        const { totalCnt = 0, oldWorkerCnt = 0, normalWorkerCnt = 0 } = res.data.data || {};
        return { todayAttendCnt: totalCnt, todayOldWorkerAttendCnt: oldWorkerCnt, todayNormalWorkerCnt: normalWorkerCnt };
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickRefresh = () => {
    refetchNewWorkerData();
    refetchAttendData();
  };

  const loading = isLoadingNewWorkerData || isLoadingAttendData || !newWorkerData || !attendData;

  if (isErrorNewWorkerData || isErrorAttendData) return <IssueGuide text={ERROR} isImg={false} />;
  return (
    <Root>
      <div className='widget-header'>
        <div className='widget-title flex-between'>
          <span>{t('금일 종합 현황')}</span>
          {!isSiteDashboard && (
            <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
              <span className='material-symbols-rounded'>refresh</span>
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <SkeletonTable rows={1} />
      ) : (
        <div className='widget-body'>
          <div className='infoBox flex-basic theme-blue'>
            <div className='theme-blue'>
              <span className='material-symbols-rounded'>person_add</span>
            </div>
            <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
              {!isSiteDashboard && <div className='infoBox-label'>{t('신규 근로자')}</div>}
              <div className={!isSiteDashboard ? 'infoBox-value' : ''}>
                <span className='count'>{newWorkerData?.newWorkerCnt ?? 0}</span>
                {t('명')}
              </div>
              {isSiteDashboard && t('신규 근로자')}
            </div>
          </div>
          <div className='infoBox flex-basic theme-blue'>
            <div className='theme-blue-sky'>
              <span className='material-symbols-rounded'>leaderboard</span>
            </div>
            <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
              {!isSiteDashboard && <div className='infoBox-label'>{t('금일 출역')}</div>}
              <div className={!isSiteDashboard ? 'infoBox-value' : ''}>
                <span className='count'>{attendData?.todayAttendCnt ?? 0}</span>
                {t('명')}
              </div>
              {isSiteDashboard && t('금일 출역')}
            </div>
          </div>
          <div className='infoBox flex-basic theme-blue'>
            <div className='theme-blue-sky'>
              <span className='material-symbols-rounded'>groups</span>
            </div>
            <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
              {!isSiteDashboard && <div className='infoBox-label'>{t('금일 고령 출역')}</div>}
              <div className={!isSiteDashboard ? 'infoBox-value' : ''}>
                <span className='count'>{attendData?.todayOldWorkerAttendCnt ?? 0}</span>
                {t('명')}
              </div>
              {isSiteDashboard && t('금일 고령 출역')}
            </div>
          </div>
          <div className='infoBox flex-basic theme-blue'>
            <div className='theme-blue'>
              <span className='material-symbols-rounded'>group</span>
            </div>
            <div className={!isSiteDashboard ? 'labelAndValue flex-between' : ''}>
              {!isSiteDashboard && <div className='infoBox-label'>{t('금일 일반 출역')}</div>}
              <div className={!isSiteDashboard ? 'infoBox-value' : ''}>
                <span className='count'>{attendData?.todayNormalWorkerCnt ?? 0}</span>
                {t('명')}
              </div>
              {isSiteDashboard && t('금일 일반 출역')}
            </div>
          </div>
        </div>
      )}
    </Root>
  );
};
